import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  Badge,
  Collapse,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  EditIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const SECTION_LABELS = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  security: "Security Policy",
  refund: "Refund Policy",
};

const PreviewLegalContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const contentData = location.state?.contentData;
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    // Handle smooth scroll with offset
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (target) {
        e.preventDefault();
        const id = target.getAttribute("href").slice(1);
        const element = document.getElementById(id);
        if (element) {
          const offsetPosition = element.offsetTop - 100;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }
    };

    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!contentData) {
    return (
      <Box p="8px" bg="gray.50" minH="100vh">
        <Box textAlign="center" py={20}>
          <Text fontSize="lg" color="gray.600">
            Content not found
          </Text>
          <Button
            mt={4}
            size="sm"
            onClick={() => navigate("/legal-content-list")}
          >
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p="8px" bg="gray.50" minH="100vh">
      {/* Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p="12px"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/legal-content-list")}
              variant="ghost"
              size="sm"
              _hover={{ bg: "gray.100" }}
            />
          </HStack>
          <HStack spacing={2}>
            <Badge
              colorScheme="purple"
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="full"
              fontWeight="medium"
            >
              Preview Mode
            </Badge>
            <Button
              leftIcon={<EditIcon />}
              size="sm"
              fontSize="xs"
              colorScheme="blue"
              onClick={() =>
                navigate(`/legal-content/edit/${id}`, {
                  state: { contentData },
                })
              }
            >
              Edit
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Main Content Container */}
      <Flex
        gap={{ base: 2, lg: 6 }}
        maxW="1400px"
        mx="auto"
        direction={{ base: "column", lg: "row" }}
      >
        {/* Left Side - Topics List */}
        <Box
          w={{ base: "100%", lg: "30%" }}
          minW="280px"
          maxH="85vh"
          overflowY="auto"
          position={{ base: "relative", lg: "sticky" }}
          top={{ base: 0, lg: "100px" }}
          bg="white"
          borderRadius="12px"
          p="24px 20px"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          css={{
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f5f9",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#cbd5e1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#94a3b8",
            },
          }}
        >
          <VStack align="stretch" spacing={2}>
            {contentData.topics?.map((topic, index) => (
              <Box key={index}>
                <Flex
                  justify="space-between"
                  align="center"
                  p="10px 12px"
                  borderRadius="6px"
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => handleToggle(index)}
                >
                  <Text
                    as="a"
                    href={`#${topic.topicId}`}
                    flex="1"
                    color="gray.800"
                    fontWeight="500"
                    fontSize="sm"
                    _hover={{ color: "blue.500" }}
                  >
                    {index + 1}. {topic.topicTitle}
                  </Text>
                  {topic.subTopics && topic.subTopics.length > 0 && (
                    <IconButton
                      aria-label="Toggle subtopics"
                      icon={
                        openIndex === index ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(index);
                      }}
                    />
                  )}
                </Flex>

                {topic.subTopics && topic.subTopics.length > 0 && (
                  <Collapse in={openIndex === index}>
                    <VStack
                      align="stretch"
                      pl="20px"
                      mt={2}
                      borderLeft="2px"
                      borderColor="gray.200"
                      spacing={1}
                    >
                      {topic.subTopics.map((subTopic, subIndex) => (
                        <Text
                          key={subIndex}
                          as="a"
                          href={`#${topic.topicId}-sub-${subIndex}`}
                          p="6px 12px"
                          borderRadius="4px"
                          fontSize="sm"
                          color="gray.600"
                          _hover={{ bg: "gray.50", color: "blue.500" }}
                          cursor="pointer"
                        >
                          {subTopic.title}
                        </Text>
                      ))}
                    </VStack>
                  </Collapse>
                )}
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Right Side - Content */}
        <Box
          w={{ base: "100%", lg: "65%" }}
          flex="1"
          bg="white"
          p={{ base: "24px 20px", md: "40px" }}
          borderRadius="12px"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
        >
          <VStack align="stretch" spacing={6}>
            {/* Main Title */}
            <Box>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="700"
                color="gray.900"
                mb={4}
                pb={4}
                borderBottom="2px"
                borderColor="gray.200"
              >
                {SECTION_LABELS[contentData.section] || contentData.section}
              </Text>
            </Box>

            {/* Topics */}
            {contentData.topics?.map((topic, index) => (
              <Box key={index} mb={6}>
                {/* Topic Title */}
                <Text
                  id={topic.topicId}
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="600"
                  color="gray.800"
                  mb={4}
                  scrollMarginTop="100px"
                >
                  {index + 1}. {topic.topicTitle}
                </Text>

                {/* Topic Content */}
                {topic.content && (
                  <Box
                    fontSize="sm"
                    lineHeight="1.7"
                    color="gray.600"
                    mb={4}
                    dangerouslySetInnerHTML={{ __html: topic.content }}
                    sx={{
                      "& p": {
                        mb: 4,
                      },
                      "& ul, & ol": {
                        ml: 6,
                        mb: 4,
                      },
                      "& li": {
                        mb: 2,
                      },
                      "& strong": {
                        color: "gray.800",
                        fontWeight: "600",
                      },
                      "& a": {
                        color: "blue.500",
                        textDecoration: "underline",
                      },
                      "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        my: 4,
                      },
                      "& th": {
                        bg: "gray.50",
                        p: 3,
                        textAlign: "left",
                        fontWeight: "600",
                        borderBottom: "2px solid",
                        borderColor: "gray.200",
                      },
                      "& td": {
                        p: 3,
                        borderBottom: "1px solid",
                        borderColor: "gray.200",
                      },
                    }}
                  />
                )}

                {/* Subtopics */}
                {topic.subTopics &&
                  topic.subTopics.map((subTopic, subIndex) => (
                    <Box key={subIndex} ml={{ base: 4, md: 6 }} mt={4}>
                      <Text
                        id={`${topic.topicId}-sub-${subIndex}`}
                        fontSize={{ base: "md", md: "lg" }}
                        fontWeight="600"
                        color="gray.700"
                        mb={3}
                        pl={5}
                        borderLeft="3px"
                        borderColor="gray.200"
                        scrollMarginTop="100px"
                      >
                        {index + 1}.{subIndex + 1} {subTopic.title}
                      </Text>
                      <Box
                        fontSize="sm"
                        lineHeight="1.7"
                        color="gray.600"
                        mb={4}
                        pl={5}
                        dangerouslySetInnerHTML={{ __html: subTopic.content }}
                        sx={{
                          "& p": {
                            mb: 4,
                          },
                          "& ul, & ol": {
                            ml: 6,
                            mb: 4,
                          },
                          "& li": {
                            mb: 2,
                          },
                          "& strong": {
                            color: "gray.800",
                            fontWeight: "600",
                          },
                          "& a": {
                            color: "blue.500",
                            textDecoration: "underline",
                          },
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            ))}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default PreviewLegalContent;
